import type { User as BAUser } from '@better-auth/core/db'

export type KeycloakData = {
  access_token: string
}

export type ApiResponse<T> = {
  data: T
  success: boolean
  message?: string
}

export type PaymentInfo = {
  payIn?: {
    type: 'card'
    last4: string
    brand?: string
    expiryMonth?: number
    expiryYear?: number
  }
  payOut?: {
    type: 'bank'
    last4: string
    bankName?: string
  }
}

export const ACCOUNT_TYPE = {
  CHECKING: 'checking',
  SAVING: 'saving',
} as const

export type AccountType = (typeof ACCOUNT_TYPE)[keyof typeof ACCOUNT_TYPE]

export type UserAccount = {
  accountHolderName?: string
  accountNumber?: string
  routingNumber?: string
  bankName?: string
  accountType?: AccountType
}

export type BankInfo = {
  id?: number
  name?: string
}

export const PET_WEIGHT = {
  UNDER_2KG: 'Under2Kg',
  FROM_2_TO_5KG: 'From2To5Kg',
  FROM_5_TO_10KG: 'From5To10Kg',
  FROM_10_TO_20KG: 'From10To20Kg',
  OVER_20KG: 'Over20Kg',
} as const

export type PetWeight = (typeof PET_WEIGHT)[keyof typeof PET_WEIGHT]

export const YES_NO_UNKNOWN = {
  NO: 'No',
  YES: 'Yes',
  I_DONT_KNOW: 'IDontKnow',
} as const

export type YesNoUnknown = (typeof YES_NO_UNKNOWN)[keyof typeof YES_NO_UNKNOWN]

export type Pet = {
  id?: number
  name?: string
  petPictureUrl?: string
  type?: string
  birthDate?: Date
  hasMicrochip?: YesNoUnknown
  lastVaccinationDate?: Date
  primaryVetId?: string
  primaryVetName?: string
  weight?: PetWeight
  isNeutered?: YesNoUnknown
}

export type PetBreed = {
  id: number
  name: string
  petType: {
    id: number
    name: string
    varName: string
  }
  isExcluded: boolean
}

export type PetVet = {
  vetId: number
  vetName: string
}

export type PolicyInvoiceInterval = 'MONTHLY' | 'QUARTERLY' | 'YEARLY'
export type PolicyStatus = 'ACTIVE' | 'CANCELED' | 'TERMINATED' | 'PENDING'
export type PolicyPlanId =
  | 'essential-care-2'
  | 'essential-1'
  | 'premier-care-plus-2'
  | 'essential-plus-1'
  | 'early-years-care-plus-2'

export type Policy = {
  id?: string
  petId?: number
  status?: PolicyStatus
  pet?: Pet
  payment?: {
    invoiceInterval?: PolicyInvoiceInterval
    recurringPayment?: number
    totalPayment?: number
    firstPayment?: number
  }
  inArrears?: {
    isInArrears: boolean
    daysInArrears: number
    amountInArrears: number
  }
  startDate?: string
  endDate?: string
  planId?: PolicyPlanId
  policyExternalId?: string
  isFreeLook?: boolean
}

export type AdditionalPartyType = 'spouse' | 'roommate' | 'pet'

export type AdditionalParty = {
  id: string
  kanguro_policy_id?: string
  type?: AdditionalPartyType
  name: string
  email?: string
  pet_type?: string
  pet_birth_date?: string
  breed_id?: number
}

export type PolicyAdditionalParty = {
  id: string
  type?: string
  fullName?: string
  email?: string
  birthDate?: string
  address?: {
    complement?: string
    streetNumber?: string
    streetName?: string
    city?: string
    state?: string
    zipCode?: string
  }
}

export type DwellingType =
  | 'SingleFamily'
  | 'MultiFamily'
  | 'Apartment'
  | 'Townhouse'
  | 'StudentHousing'
export type AdditionalCoverageType =
  | 'ReplacementCost'
  | 'WaterSewerBackup'
  | 'FraudProtection'
export type RentersPolicyStatus =
  | 'PENDING'
  | 'ACTIVE'
  | 'CANCELED'
  | 'TERMINATED'
export type InvoiceInterval = 'YEARLY' | 'MONTHLY' | 'QUARTERLY'

export type RentersPolicy = {
  id?: string
  dwellingType?: DwellingType
  onboardingCompleted?: boolean
  address?: {
    zipCode?: number
    state?: string
    city?: string
    address?: string
    complement?: string
  }
  createdAt?: string
  startAt?: string
  endAt?: string
  planSummary?: {
    liability?: { id?: number; value?: number }
    deductible?: { id?: number; value?: number }
    personalProperty?: { id?: number; value?: number }
    lossOfUse?: { id?: number; value?: number }
  }
  additionalCoverages?: {
    type?: AdditionalCoverageType
    coverageLimit?: number
    deductibleLimit?: number
  }[]
  payment?: {
    totalFees?: number
    totalPayment?: number
    invoiceInterval?: InvoiceInterval
    totalDiscounts?: number
    totalPaymentWithoutFees?: number
    firstPayment?: number
    recurringPayment?: number
    stateId?: string
  }
  offerId?: number
  policyExternalId?: number
  isInsuranceRequired?: boolean
  status?: RentersPolicyStatus
  inArrears?: {
    isInArrears?: boolean
    daysInArrears?: number
    amountInArrears?: number
  }
  freeLookPeriod?: boolean
  additionalParties?: PolicyAdditionalParty[]
}

export type OnboardingTrace = {
  id: string
  user_id: string
  status: 'in_progress' | 'completed'
  product_type: 'pet' | 'renters'
  step: number
  external_id?: number
}

// The server scopes traces to the logged-in user, so only the product is sent
export type OnboardingTraceFilters = {
  product_type: OnboardingTrace['product_type']
}

type OnboardingValidationItem = {
  showOnboarding: boolean
}

export type OnboardingValidation = {
  pet: OnboardingValidationItem
  renters: OnboardingValidationItem
}

export type UserLanguage = 'English' | 'Spanish'

export type UserGender = 'Man' | 'Woman' | 'NonBinary' | 'PreferNotToSay'

export type UserContactChannel = 'whatsapp' | 'sms' | 'email' | 'phone'

export type UserPreferences = {
  language?: UserLanguage
  talkedLanguage?: UserLanguage
  gender?: UserGender
  contactChannels?: UserContactChannel[]
}

export type User = {
  id: string
  givenName: string
  surname: string
  language: UserLanguage
  gender?: UserGender
  contactChannels?: UserContactChannel[]
  referralCode: string
  phone: string
  address: string
  city: string
  state: string
  complement: string
  zipCode: string
  email: string
  birthDate: string
  isNeededDeleteData: boolean
  hasAccessBlocked: boolean
  fullName: string
  fullAddress: string
  insuranceId: number
  name?: string
  emailVerified?: boolean
  image?: string | undefined
  createdAt?: Date
  updatedAt?: Date
  ciUserId?: string
  kanguroUserId?: string
  betterAuthUserId?: string
} & UserPreferences

export interface BetterAuthUser extends Partial<BAUser> {
  ciUserId?: string
  kanguroUserId?: string
}

type CiIframeConfig = {
  partner: string
  product: string
  environment: string
  customPayload: { accessToken: string }
  appendHostQueryParams: boolean
  iframeSettings: { resizeHeightOffset: number; scrollTopOffset: number }
  containerId: string
}

declare global {
  interface Window {
    ciIframeSnippet: {
      HandlerInstance: { init: (config: CiIframeConfig) => void }
    }
  }
}

// Kanguro Claims API (pet). Field names are snake_case as on the wire.
export const PET_CLAIM_STATUS = {
  // Draft statuses, derived from what the claim holds
  PARTIAL: 'partial',
  FILEABLE: 'fileable',
  // Insurance core statuses once submitted. The set is open: anything the API
  // has no word for comes back as `filed`
  SUBMITTED: 'submitted',
  IN_REVIEW: 'in_review',
  PENDING_MEDICAL_HISTORY: 'pending_medical_history',
  MEDICAL_HISTORY_IN_REVIEW: 'medical_history_in_review',
  ASSIGNED: 'assigned',
  APPROVED: 'approved',
  PAID: 'paid',
  DENIED: 'denied',
  CLOSED: 'closed',
  DELETED: 'deleted',
  FILED: 'filed',
} as const

// Open set: statuses after `filed` come from the insurance core
export type PetClaimStatus =
  | (typeof PET_CLAIM_STATUS)[keyof typeof PET_CLAIM_STATUS]
  | (string & {})

export type ClaimMissingField = {
  code: string
  label: string
}

export const CLAIM_DOCUMENT_TYPE = {
  INVOICE: 'invoice',
  PLEDGE_OF_HONOR: 'pledge_of_honor',
  PET_PICTURE: 'pet_picture',
  // Attached inside the insurance core (medical records, OFAC checks, ...)
  UNKNOWN: 'unknown',
} as const

export type ClaimDocumentType =
  | (typeof CLAIM_DOCUMENT_TYPE)[keyof typeof CLAIM_DOCUMENT_TYPE]
  | (string & {})

export type ClaimDocument = {
  id: string
  type: ClaimDocumentType
  file_name: string
  content_type: string
  size_bytes: number
  created_at?: string
  url: string
}

export const PET_CLAIM_LIST_KIND = {
  DRAFTS: 'drafts',
  FILED: 'filed',
} as const

export type PetClaimListKind =
  (typeof PET_CLAIM_LIST_KIND)[keyof typeof PET_CLAIM_LIST_KIND]

export type Pagination = {
  page: number
  per_page: number
  total: number
  total_pages: number
}

export type PetClaimList = {
  data: PetClaim[]
  pagination: Pagination
}

export type PetClaim = {
  id: string
  policy_id: number
  status: PetClaimStatus
  missing_fields: ClaimMissingField[]
  amount?: number
  invoice_date?: string
  description?: string
  core_claim_id?: string
  filed_at?: string
  created_at: string
  updated_at: string
}

export type RentersClaim = {
  id: string
  status?: string
  customer_description?: string
  date_of_loss?: string
  policy_id_ci?: string
}
